/**
 * Settings (`/settings`) — tabbladen General · Users · Compensation & activity
 * · Model portal.
 *
 * 28-10-2026 (Jordy): "Compensation & activity" verhuisde van Chatters naar
 * hier (components/CompensatieInstellingen.tsx) en vervangt het oude tabblad
 * "Chatters". Het tabblad en de compensatievelden in de Users-tabel zijn
 * alleen voor owner/admin; ?tab=chatters leidt naar het nieuwe tabblad.
 *
 * Endpoints: dashboard/system-status · auth/users · dashboard/agencies ·
 * chatter-finance/compensation-roster · models/chatters ·
 * dashboard/chatter-floor-exclusions (de Mass DM-filterpreset, sinds
 * 30-10-2026 via MassDmPresetForm) en de losse instellingen onder
 * dashboard/settings/<sleutel> (smtp_*, humanizer_fast_mode).
 */
import { useEffect, useState } from 'react'
import { Avatar, Bar, ErrorNote, Loading, Panel, Plat, Seg, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { haalBestand } from '../lib/api'
import { redenUit, useAiStand } from '../lib/aiStatus'
import { useActions, mut } from '../lib/actions'
import { Modal, Field } from '../components/Modal'
import { RechtenBlok } from '../components/RechtenBlok'
import { standaardTokens } from '../lib/rechtenBlok'
import { VerwijderDialog } from '../components/VerwijderDialog'
import { ModelPortalBeheer } from '../components/ModelPortalBeheer'
import { isVolledigBeheer, useMij } from '../lib/rechten'
import { CompensatieInstellingen } from '../components/CompensatieInstellingen'
import { MassDmPresetForm } from '../components/MassDmPresetDialog'
import { ZoneKeuze } from '../components/ZoneKeuze'
import { SCHAAL_KEUZES, useSchaalKeuze } from '../lib/schaal'
import { api } from '../lib/api'
import { dt, dtSec, num, pct } from '../lib/format'
import { useNavigate, useSearchParams} from 'react-router-dom'

interface SystemStatus {
  status: string
  system: {
    platform: string; python_version: string; cpu_percent: number
    memory: { total_mb: number; used_mb: number; percent: number }
    disk: { total_gb: number; used_gb: number; percent: number }
  }
  process?: { memory_mb: number; pid: number; uptime_seconds: number }
  application?: Record<string, unknown>
}
interface User {
  id: number; username: string; email: string; display_name: string | null
  role: string; active: boolean; linked_chatter_id: number | null
  allowed_pages: string[] | null
  fancentro_credentials_configured: boolean; last_login_at: string | null
  /** 07-10-2026: IANA-tijdzone voor Mass Messages (null = Amsterdam). */
  timezone?: string | null
  /** 23-10-2026 (Content fase 2): max per download en eigen 24-uursgrens. */
  content_download_max?: number | null
  download_cap?: number | null
}
interface Agency {
  id: number; name: string; platform: string; email: string
  is_active: boolean; is_test: boolean; connected_creators: number
  last_connected_at: string | null
}
/** Alleen wat de Users-tabel uit compensation-roster toont. */
interface Comp {
  chatter_id: number; commission_pct: number | null
  default_commission_pct: number; payout_wallet_address: string | null
}
interface Setting { key: string; value: string }

// 05-10-2026 (Jordy): de rechten (pagina's, schuiven, filters) staan in
// lib/rechtenBlok.ts en worden getoond door components/RechtenBlok.tsx.

/** Dezelfde vier rollen met dezelfde omschrijving als op oud. */
const ROLLEN = [
  { value: 'owner', label: 'Owner (full access)' },
  { value: 'manager', label: 'Manager (no settings/users)' },
  { value: 'chatter', label: 'Chatter (conversations + assigned models)' },
  { value: 'viewer', label: 'Viewer (read-only)' },
]

const TABS = [
  { value: 'general', label: 'General' },
  { value: 'users', label: 'Users' },
  // 28-10-2026 (Jordy): was 'chatters' ("Chatter Management"); alleen voor
  // owner/admin, zie `tabs` in de component.
  { value: 'compensation', label: 'Compensation & activity' },
  // 02-10-2026 (fase 2): wie van de modellen op /portal kan inloggen.
  { value: 'portal', label: 'Model portal' },
]

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <span className={on ? 'sw-t on' : 'sw-t'} onClick={onClick} />
}
function uptime(sec: number | undefined): string {
  const s = Number(sec ?? 0)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${h}h ${m}m`
}

/** Wat er te downloaden is. De server bewaakt dezelfde lijst; dit is alleen
 *  wat het scherm toont. */
const DOWNLOADS = [
  { naam: 'Trillion-pakket.md',
    wat: 'all documents in one file, with a heading per section' },
  { naam: 'voor-zilvester.zip',
    wat: 'all separate files plus openapi.json, as a zip' },
  { naam: 'LEESMIJ.md', wat: 'guide to the files' },
  { naam: 'antwoord-07-09.md',
    wat: 'reply to aanjordyvolledig.md: access, freshness, A1-A6, agreements' },
  { naam: 'api-voor-trillion.md', wat: 'the manual' },
  { naam: 'afspraken.md', wat: 'what the account may do, rate, certificate, phase 2' },
  { naam: 'leesbare-endpoints.md', wat: 'measured list of what the token may read' },
]

/** Wat /api/data-freshness/data-checks teruggeeft. */
interface DataChecks {
  ran_at: string | null
  ok: boolean | null
  failed?: string[]
  note?: string
  checks: { name: string; ok: boolean; detail: string }[]
}

interface ApiTokenRij {
  id: number
  name: string
  hint: string
  scopes: string
  instance: string
  active: boolean
  last_used_at: string | null
  last_used_ip: string | null
  use_count: number
  note: string | null
}

interface Grenzen {
  fancentro: { min: number; max: number; eenheid: string }
  f2f: { min: number; max: number; eenheid: string }
}

/** 18-10-2026 (Jordy): de grootte-keuze deed niets; nu dezelfde voorkeur als de topbalk. */
function DashboardSize() {
  const [keuze, zet] = useSchaalKeuze()
  return (
    <select className="inp" value={keuze} onChange={(e) => zet(e.target.value)}>
      {SCHAAL_KEUZES.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
    </select>
  )
}

export default function Settings() {
  const ai = useAiStand()
  const reden = redenUit(ai.data)
  const grenzen = useApi<Grenzen>('/api/admin/paid-posting/limits')
  const tokens = useApi<ApiTokenRij[]>('/api/admin/api-tokens')
  /** De uitkomst van de nachtelijke vergelijking met oud. Alleen lezen: het
   *  rekenwerk gebeurt in scripts/data_checks.py, niet hier. */
  const checks = useApi<DataChecks>('/api/data-freshness/data-checks')
  const [bezigMet, setBezigMet] = useState<string | null>(null)
  const [link, setLink] = useState<{ naam: string; url: string } | null>(null)
  const [fcMax, setFcMax] = useState('')
  const [f2fMax, setF2fMax] = useState('')
  useEffect(() => {
    if (!grenzen.data) return
    setFcMax(String(grenzen.data.fancentro.max))
    setF2fMax(String(grenzen.data.f2f.max))
  }, [grenzen.data])
  const navigate = useNavigate()
  /* Het tabblad staat in het adres (?tab=users). Zonder dat verspringt de
     pagina bij elke herlaad terug naar General, en kun je een tabblad niet
     doorsturen naar iemand anders. */
  const [zoek, setZoek] = useSearchParams()
  const ikBen = useMij().data
  // 28-10-2026 (Jordy): compensatie alleen voor owner/admin.
  const volledig = isVolledigBeheer(ikBen)
  const tabs = TABS.filter((o) => o.value !== 'compensation' || volledig)
  // De oude deep link ?tab=chatters komt uit op het nieuwe tabblad.
  const gevraagd = zoek.get('tab') === 'chatters' ? 'compensation' : zoek.get('tab')
  const tab = tabs.some((o) => o.value === gevraagd)
    ? (gevraagd as string)
    : 'general'
  const setTab = (v: string) => setZoek(v === 'general' ? {} : { tab: v },
                                        { replace: true })
  const [fast, setFast] = useState(true)
  const [tls, setTls] = useState(true)
  const [smtp, setSmtp] = useState<Record<string, string>>({})
  const [testTo, setTestTo] = useState('')
  /* Gebruikersbeheer. Op oud kon je hier een gebruiker aanmaken, zijn rol
     wijzigen, hem aan een chatterprofiel koppelen, de paginarechten zetten en
     een nieuw wachtwoord geven. Op nieuw stond alleen een lijst met een
     aan/uit-knop, en de knop "+ Add User" gooide met opzet een fout op
     ("Gebruik het gebruikersformulier hieronder") terwijl dat formulier er
     niet was. */
  const [userNieuw, setUserNieuw] = useState(false)
  const [userEdit, setUserEdit] = useState<User | null>(null)
  /** 30-09-2026 (Jordy): een login definitief verwijderen, alleen de owner. */
  const [userWeg, setUserWeg] = useState<User | null>(null)
  const ikBenOwner = String(ikBen?.role ?? '').toLowerCase() === 'owner'
  // 07-10-2026 (Jordy): een admin zet in Edit user alleen de schuif "Mass
  // Messages page" (eigen route; PATCH /users/{id} blijft alleen owner).
  const ikBenAdmin = String(ikBen?.role ?? '').toLowerCase() === 'admin'
  const leegFormulier = {
    username: '', email: '', display_name: '', password: '',
    role: 'manager', linked_chatter_id: '' as string,
    allowed_pages: [] as string[], active: true,
    timezone: '',
    content_download_max: '',
    download_cap: '',
  }
  const [userForm, setUserForm] = useState(leegFormulier)
  const [agencyOpen, setAgencyOpen] = useState(false)
  const [agency, setAgency] = useState({
    name: '', platform: 'fancentro', email: '', password: '',
    totp_secret: '', is_active: true, is_test: false,
  })
  const { run, busy, notify } = useActions()

  /** Elke instelling gaat als losse PUT naar dashboard/settings/<sleutel>,
   *  precies zoals de bundle het doet. */
  const saveSetting = (key: string, value: unknown) =>
    mut.put(`/api/admin/dashboard/settings/${key}`, { value })

  const sys = useApi<SystemStatus>('/api/admin/dashboard/system-status')
  const users = useApi<User[]>('/api/admin/auth/users')
  const agencies = useApi<Agency[]>('/api/admin/dashboard/agencies')
  // Commissie en wallet in de Users-tabel: alleen voor owner/admin opvragen
  // (de server geeft anderen 403).
  const comp = useApi<Comp[]>(
    volledig ? '/api/admin/chatter-finance/compensation-roster' : null, [volledig])
  const chatters = useApi<{ id: number; name: string }[]>('/api/admin/models/chatters')
  const smtpHost = useApi<Setting>('/api/admin/dashboard/settings/smtp_host')
  const smtpPort = useApi<Setting>('/api/admin/dashboard/settings/smtp_port')
  const smtpUser = useApi<Setting>('/api/admin/dashboard/settings/smtp_user')
  const smtpFrom = useApi<Setting>('/api/admin/dashboard/settings/smtp_from')

  const mem = sys.data?.system.memory
  const disk = sys.data?.system.disk

  return (
    <>
      <div className="filters" style={{ justifyContent: 'flex-end' }}>
        {/* UITGEGRIJSD, met opzet.
            Deze knop riep /api/admin/dashboard/restart-chat-service aan. Die
            route bestaat op deze server niet (405). De route die er wél is,
            /dashboard/restart, draait nog het script en de unitnamen van de
            OUDE server: /opt/bots/restart-dashboards.sh en
            `systemctl restart fancentro-chatbot`. Geen van drieën bestaat
            hier. Het gevolg was een knop die niets deed en toch "Service
            restart initiated" meldde.

            Aankoppelen aan de echte unit zou `novahub-shadow-nova` herstarten,
            en dat is de POSTING-worker: die houdt headless browsers vast die
            midden in een upload naar FanCentro kunnen zitten. Op oud was er
            één gecombineerde chatbot-dienst; hier delen chat en posting één
            worker, dus een herstart van "alleen de chatluisteraar" bestaat
            niet. Daarom uit, met de uitleg in beeld, tot Jordy beslist. */}
        <button className="btn ghost sm" type="button" disabled
                title={'Not available on this server. On the old server this '
                       + 'restarted the combined chatbot service. Here chat and '
                       + 'posting share one worker, so restarting it would '
                       + 'interrupt posting. Ask Jordy before restarting the worker.'}>
          Restart Chat Service
        </button>
        <button className="btn ghost sm" type="button"
                onClick={() => { sys.refetch(); users.refetch(); agencies.refetch(); comp.refetch() }}>
          Refresh
        </button>
      </div>

      <div className="filters">
        <Seg options={tabs} value={tab} onChange={setTab} />
      </div>

      <>
      {/* Elk blok hoort bij één tabblad. Vóór vandaag stond alles
          altijd op het scherm en deed de tabbalk niets: `tab` werd wel
          gezet maar nergens gelezen. */}
      {tab === 'general' ? (
        <>
          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Panel title="Testing Mode">
              <div className="between">
                <div>
                  <b>Instant AI replies</b>
                  <div className="mut2" style={{ fontSize: 12, maxWidth: 340 }}>
                    1–3s instead of the natural 30–120s persona delays. Turn OFF
                    before going live with real fans.
                  </div>
                </div>
                <Toggle
                  on={fast}
                  onClick={() => {
                    const next = !fast
                    setFast(next)
                    run({
                      label: 'Testing Mode',
                      request: saveSetting('humanizer_fast_mode', next ? '1' : '0'),
                      successText: next ? 'Instant replies ON' : 'Instant replies OFF',
                    })
                  }}
                />
              </div>
            </Panel>
            <Panel title="Dashboard size">
              <div className="field">
                <label>Size</label>
                <DashboardSize />
                <span className="hint">
                  Saved for your account on every device. Same control in the top
                  bar, next to AI ON/OFF.
                </span>
              </div>
            </Panel>
          </div>

          {/* De dagelijkse gegevenscontrole. Draait elke ochtend om 06:30 en
              schrijft zijn uitkomst weg; dit paneel leest die alleen, zodat het
              scherm niet minuten hoeft te wachten op een vergelijking met oud. */}
          <Panel
            title="Data checks"
            desc="Daily comparison against the old server: row counts, freshness and the earnings parity."
            right={checks.data?.ok === undefined || checks.data?.ok === null
              ? <Tag kind="mut">not run yet</Tag>
              : checks.data.ok
                ? <Tag kind="ok"><span className="dotst on-live" />all green</Tag>
                : <Tag kind="bad">{(checks.data.failed ?? []).length} failing</Tag>}
          >
            {checks.loading ? <Loading /> : !checks.data?.checks?.length ? (
              <div className="empty">
                {checks.data?.note ?? 'The check has not run yet.'}
              </div>
            ) : (
              <>
                <div className="mut2" style={{ fontSize: 12, marginBottom: 8 }}>
                  Last run {checks.data.ran_at ? dt(checks.data.ran_at) : '—'}
                </div>
                <Table cols={[
                  { key: 's', label: '' }, { key: 'n', label: 'Check' },
                  { key: 'd', label: 'Detail' },
                ]}>
                  {checks.data.checks.map((c) => (
                    <tr key={c.name}>
                      <td style={{ width: 30 }}>
                        {c.ok ? <Tag kind="ok">ok</Tag> : <Tag kind="bad">!</Tag>}
                      </td>
                      <td>{c.name}</td>
                      <td className="mut2" style={{ fontSize: 12 }}>{c.detail}</td>
                    </tr>
                  ))}
                </Table>
              </>
            )}
          </Panel>

          {/* Downloads. Een directe URL werkt niet in de browser — die stuurt
              geen token mee — dus haalt de knop het bestand op mét token. De
              tweede knop maakt een eenmalige link voor wie tóch een gewone
              browserdownload wil (of het bestand aan iemand anders geeft). */}
          <Panel
            title="Downloads"
            desc="Files behind the login. Owner only; links expire after 24 hours."
          >
            {DOWNLOADS.map((d) => (
              <div className="row" key={d.naam}
                   style={{ gap: 10, alignItems: 'center', marginBottom: 10,
                            flexWrap: 'wrap' }}>
                <b style={{ fontSize: 13, minWidth: 190 }}>{d.naam}</b>
                <span className="mut2" style={{ fontSize: 12, flex: 1,
                                                minWidth: 200 }}>{d.wat}</span>
                <button className="btn primary sm" type="button"
                        disabled={bezigMet === d.naam}
                        onClick={async () => {
                          setBezigMet(d.naam)
                          try {
                            await haalBestand(`/api/admin/downloads/${d.naam}`, d.naam)
                            notify(`${d.naam} downloaded.`)
                          } catch (fout) {
                            notify(`Download failed: ${fout instanceof Error ? fout.message : fout}`,
                                   false)
                          } finally { setBezigMet(null) }
                        }}>
                  {bezigMet === d.naam ? 'Working…' : 'Download'}
                </button>
                <button className="btn ghost sm" type="button"
                        onClick={async () => {
                          try {
                            const uit = await api.post<{ url: string }>(
                              `/api/admin/downloads/${d.naam}/link`)
                            setLink({ naam: d.naam, url: window.location.origin + uit.url })
                          } catch (fout) {
                            notify(`Creating the link failed: ${fout instanceof Error ? fout.message : fout}`,
                                   false)
                          }
                        }}>
                  Create download link
                </button>
              </div>
            ))}
            {link ? (
              <div className="note" style={{ marginTop: 10 }}>
                <b>One-time link for {link.naam}</b> — valid for ten minutes and
                usable exactly once. After that it is dead; create a
                new one.
                <input className="inp" readOnly value={link.url}
                       style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 12 }}
                       onFocus={(e) => e.currentTarget.select()} />
              </div>
            ) : null}
          </Panel>

          {/* Serviceaccounts. Hier zie je of en wanneer Trillion leest. */}
          <Panel
            title="API service accounts"
            desc="Tokens for external parties. Read-only; every change via the API is refused."
            flush
          >
            {tokens.loading ? <Loading /> : tokens.error ? (
              <ErrorNote error={tokens.error} />
            ) : !(tokens.data ?? []).length ? (
              <div className="note" style={{ margin: 16 }}>No tokens yet.</div>
            ) : (
              <Table cols={[
                { key: 'n', label: 'Name' }, { key: 'h', label: 'Token' },
                { key: 's', label: 'Scopes' }, { key: 'i', label: 'Instance' },
                { key: 'l', label: 'Last used' }, { key: 'c', label: 'Calls', num: true },
                { key: 'a', label: 'Status' }, { key: 'x', label: '' },
              ]}>
                {(tokens.data ?? []).map((t) => (
                  <tr key={t.id}>
                    <td><b>{t.name}</b>
                      {t.note ? <div className="mut2" style={{ fontSize: 12 }}>{t.note}</div> : null}
                    </td>
                    <td className="mut mono">{t.hint}</td>
                    <td className="mut">{t.scopes}</td>
                    <td className="mut">{t.instance}</td>
                    <td className="mut mono">
                      {t.last_used_at ? dtSec(t.last_used_at) : <span className="dim">never</span>}
                      {t.last_used_ip ? <div className="mut2" style={{ fontSize: 11 }}>{t.last_used_ip}</div> : null}
                    </td>
                    <td className="num mono">{num(t.use_count)}</td>
                    <td>{t.active
                      ? <Tag kind="ok">active</Tag>
                      : <Tag kind="bad">revoked</Tag>}</td>
                    <td>
                      {t.active ? (
                        <button className="btn danger sm" type="button"
                                disabled={busy === 'Revoke token'}
                                onClick={() => run({
                                  label: 'Revoke token',
                                  confirm: `Revoke token "${t.name}"? From that moment it no longer `
                                    + 'works, and this cannot be undone.',
                                  request: mut.post(`/api/admin/api-tokens/${t.id}/revoke`),
                                  onDone: tokens.refetch,
                                  successText: `Token ${t.name} revoked.`,
                                })}>
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </Table>
            )}
            <div className="note" style={{ margin: 16 }}>
              A token can be read only once: when it is created. After that only
              a hash is stored in the database. Lost means a new token.
            </div>
          </Panel>

          {/* De belangrijkste schakelaar van het hele dashboard. Bewust
              bovenaan, met de gevolgen erbij en een bevestiging ervoor: hier
              hangt aan of er antwoorden vanzelf naar echte fans gaan. */}
          <Panel
            title="AI chatbot (all conversations)"
            desc="Main switch. The AI only replies when this is on and the chat itself is set to AI."
          >
            <div className="row" style={{ alignItems: 'center', gap: 14,
                                          flexWrap: 'wrap' }}>
              <span className={ai.data?.effective ? 'aistand aan' : 'aistand uit'}>
                <span className="stip" />
                AI {ai.data?.effective ? 'ON' : 'OFF'}
              </span>
              <span className="mut2" style={{ fontSize: 12.5 }}>
                {num(ai.data?.conversations_ai_on ?? 0)} of{' '}
                {num(ai.data?.conversations_total ?? 0)} chats are set to AI
              </span>
              <span className="spacer" style={{ flex: 1 }} />
              <button
                className={ai.data?.switch_enabled ? 'btn danger sm' : 'btn primary sm'}
                type="button"
                disabled={busy === 'AI chatbot' || !ai.data}
                onClick={() => run({
                  label: 'AI chatbot',
                  confirm: ai.data?.switch_enabled
                    ? 'Turn the AI chatbot OFF for all chats?'
                    : 'Turn the AI chatbot ON? From that moment the bot replies '
                      + 'on its own in every chat set to AI — currently '
                      + `${ai.data?.conversations_ai_on ?? 0}.`,
                  request: mut.put('/api/admin/ai-control',
                                   { enabled: !ai.data?.switch_enabled }),
                  onDone: ai.refetch,
                  successText: ai.data?.switch_enabled
                    ? 'AI chatbot turned off.' : 'AI chatbot turned on.',
                })}
              >
                {ai.data?.switch_enabled ? 'Turn AI off' : 'Turn AI on'}
              </button>
            </div>

            {reden ? (
              <div className="note" style={{ marginTop: 12 }}>{reden}</div>
            ) : null}

            <div className="note" style={{ marginTop: 12 }}>
              Two locks that must both be open:{' '}
              <b>server {ai.data?.env_enabled ? 'allows it' : 'does not allow it'}</b>
              {' · '}
              <b>switch {ai.data?.switch_enabled ? 'on' : 'off'}</b>.
              Chat connections{' '}
              {ai.data?.chat_live ? 'are on' : 'are off'} (CHAT_LIVE) —
              independent of this: chatters can work while the bot
              stays silent.
            </div>

            <div className="row" style={{ marginTop: 12, gap: 10,
                                          alignItems: 'center' }}>
              <button className="btn ghost sm" type="button"
                      disabled={busy === 'AI off per chat'}
                      onClick={() => run({
                        label: 'AI off per chat',
                        confirm: `Turn off the AI in all ${ai.data?.conversations_ai_on ?? 0} `
                          + 'chats? This belongs to the '
                          + 'cutover. Turning it back on is per chat with Resume AI.',
                        request: mut.post('/api/admin/ai-control/all-conversations-off'),
                        onDone: ai.refetch,
                        successText: 'All chats now have AI off.',
                      })}>
                Turn AI off in all chats
              </button>
              <span className="mut2" style={{ fontSize: 12 }}>
                Only changes `ai_opted_in`; nothing is deleted.
              </span>
            </div>
          </Panel>

          {/* De bovengrens is een vangnet tegen een typefout van drie nullen.
              Het minimum staat vast — dat komt van het platform zelf. */}
          <Panel
            title="Paid posting price limits"
            desc="Upper bounds for a paid post. The minimum comes from the platform and cannot be changed."
          >
            <div className="grid two">
              <div className="field">
                <label>FanCentro maximum (USD)</label>
                <input className="inp" type="number" min={0.99} step="0.01"
                       value={fcMax}
                       onChange={(e) => setFcMax(e.target.value)} />
                <span className="hint">
                  Minimum ${grenzen.data?.fancentro.min ?? '0.99'} — set by FanCentro.
                </span>
              </div>
              <div className="field">
                <label>F2F maximum (EUR)</label>
                <input className="inp" type="number" min={1} step="0.01"
                       value={f2fMax}
                       onChange={(e) => setF2fMax(e.target.value)} />
                <span className="hint">
                  Minimum €{grenzen.data?.f2f.min ?? '1.00'} — set by F2F.
                </span>
              </div>
            </div>
            <button className="btn primary sm" type="button"
                    disabled={busy === 'Price limits'}
                    onClick={() => run({
                      label: 'Price limits',
                      request: mut.put('/api/admin/paid-posting/limits', {
                        fancentro_max_usd: Number(fcMax) || undefined,
                        f2f_max_eur: Number(f2fMax) || undefined,
                      }),
                      onDone: grenzen.refetch,
                      successText: 'Price limits saved.',
                    })}>
              Save limits
            </button>
          </Panel>

          <Panel
            title="Mass DM filter preset (regular chatters)"
            desc="Pre-setup for chatters who cannot edit filters."
          >
            {/* 30-10-2026 (Jordy): hetzelfde formulier als de dialoog op
                Chatters; leest en schrijft de afgedwongen
                chatter_floor_exclusions (niet meer mass_dm_filter_preset). */}
            <MassDmPresetForm />
          </Panel>

          <Panel title="SMTP / Outbound Email" desc="Required for password-reset emails.">
            <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="field">
                <label>Host</label>
                <input className="inp" placeholder="smtp.example.com"
                       value={smtp.smtp_host ?? smtpHost.data?.value ?? ''}
                       onChange={(e) => setSmtp((v) => ({ ...v, smtp_host: e.target.value }))} />
              </div>
              <div className="field">
                <label>Port</label>
                <input className="inp" placeholder="587"
                       value={smtp.smtp_port ?? smtpPort.data?.value ?? ''}
                       onChange={(e) => setSmtp((v) => ({ ...v, smtp_port: e.target.value }))} />
                <span className="hint">465 = SMTPS · 587 = STARTTLS</span>
              </div>
              <div className="field">
                <label>Username</label>
                <input className="inp"
                       value={smtp.smtp_user ?? smtpUser.data?.value ?? ''}
                       onChange={(e) => setSmtp((v) => ({ ...v, smtp_user: e.target.value }))} />
              </div>
              <div className="field">
                <label>Password</label>
                <input className="inp" type="password" placeholder="••••••••"
                       value={smtp.smtp_password ?? ''}
                       onChange={(e) => setSmtp((v) => ({ ...v, smtp_password: e.target.value }))} />
              </div>
              <div className="field">
                <label>From address</label>
                <input className="inp" placeholder="noreply@nova…"
                       value={smtp.smtp_from ?? smtpFrom.data?.value ?? ''}
                       onChange={(e) => setSmtp((v) => ({ ...v, smtp_from: e.target.value }))} />
              </div>
              <div className="field">
                <label>Send test email to</label>
                <div className="row">
                  <input className="inp" placeholder="you@example.com" value={testTo}
                         onChange={(e) => setTestTo(e.target.value)} />
                  <button
                    className="btn sm"
                    type="button"
                    disabled={!testTo || busy === 'Send test'}
                    onClick={() => run({
                      label: 'Send test',
                      request: mut.post('/api/admin/dashboard/settings/smtp-test', { to: testTo }),
                      successText: `Test email sent to ${testTo}.`,
                    })}
                  >
                    Send test
                  </button>
                </div>
              </div>
            </div>
            <label className="chkrow">
              <span className={tls ? 'chk on' : 'chk'} onClick={() => setTls((v) => !v)}>
                {tls ? '✓' : ''}
              </span>
              Use TLS (recommended for port 587; ignored on 465)
            </label>
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                className="btn primary sm"
                type="button"
                disabled={busy === 'Save SMTP settings'}
                onClick={() => run({
                  label: 'Save SMTP settings',
                  request: async () => {
                    const vals: Record<string, string> = {
                      smtp_host: smtp.smtp_host ?? smtpHost.data?.value ?? '',
                      smtp_port: smtp.smtp_port ?? smtpPort.data?.value ?? '',
                      smtp_user: smtp.smtp_user ?? smtpUser.data?.value ?? '',
                      smtp_from: smtp.smtp_from ?? smtpFrom.data?.value ?? '',
                      smtp_use_tls: tls ? '1' : '0',
                    }
                    if (smtp.smtp_password) vals.smtp_password = smtp.smtp_password
                    for (const [k, v] of Object.entries(vals)) await saveSetting(k, v)()
                  },
                  onDone: () => { smtpHost.refetch(); smtpPort.refetch(); smtpUser.refetch(); smtpFrom.refetch() },
                  successText: 'SMTP settings saved.',
                })}
              >
                Save SMTP settings
              </button>
            </div>
          </Panel>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Panel title="System Status">
              {sys.loading ? <Loading /> : sys.error ? <ErrorNote error={sys.error} /> : (
                <>
                  <div className="mini" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
                    <div className="f">
                      <small>Status</small>
                      <b style={{ color: 'var(--ok)' }}>{sys.data?.status}</b>
                    </div>
                    <div className="f"><small>Platform</small><b>{sys.data?.system.platform}</b></div>
                    <div className="f"><small>Python</small><b>{sys.data?.system.python_version}</b></div>
                    <div className="f"><small>Uptime</small><b>{uptime(sys.data?.process?.uptime_seconds)}</b></div>
                    <div className="f">
                      <small>Process Memory</small>
                      <b>{Number(sys.data?.process?.memory_mb ?? 0).toFixed(1)} MB</b>
                    </div>
                    <div className="f"><small>Process ID</small><b>{sys.data?.process?.pid ?? '—'}</b></div>
                  </div>
                  <div className="divh" />
                  <div className="mut2" style={{ fontSize: 12, marginBottom: 8 }}>Server Resources</div>
                  <div className="bars">
                    <Bar label="CPU" value={Math.round(sys.data?.system.cpu_percent ?? 0)} max={100} />
                    <Bar label="Memory (GB)"
                         value={Number(((mem?.used_mb ?? 0) / 1024).toFixed(1))}
                         max={Number(((mem?.total_mb ?? 1) / 1024).toFixed(1))} />
                    <Bar label="Disk (GB)"
                         value={Number((disk?.used_gb ?? 0).toFixed(1))}
                         max={Number((disk?.total_gb ?? 1).toFixed(1))} />
                  </div>
                </>
              )}
            </Panel>
            <Panel title="Platform Connections" flush>
              <div className="feed">
                {[
                  ['FanCentro', 'Connected'], ['f2f.com', 'Connected (Monitor)'],
                  ['Telegram Bot', 'Connected'], ['OpenAI API', 'Active'],
                  ['Posting Bot', 'Running'],
                ].map(([name, state]) => (
                  <div className="it" key={name}>
                    <span className="st tag ok">{name}</span>
                    <div className="mut">{state}</div>
                    <span className="tm"><span className="dotst on-live" /></span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <Panel title="Application">
            <div className="mini" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
              <div className="f"><small>API Port</small><b>8100</b></div>
              <div className="f"><small>Dashboard Port</small><b>8101</b></div>
              <div className="f"><small>Agencies</small><b>{num(agencies.data?.length)}</b></div>
              <div className="f"><small>Users</small><b>{num(users.data?.length)}</b></div>
            </div>
          </Panel>
        </>
      ) : null}

      {tab === 'portal' ? <ModelPortalBeheer /> : null}

      {tab === 'users' ? (
        <>
          <Panel title="User Management" flush
               right={<button className="btn primary sm" type="button"
                              onClick={() => { setUserForm(leegFormulier); setUserNieuw(true) }}>
                        + Add User
                      </button>}>
          {users.loading ? <Loading /> : users.error ? <ErrorNote error={users.error} /> : (
            <Table cols={[
              { key: 'u', label: 'Username' }, { key: 'e', label: 'Email' },
              { key: 'd', label: 'Display Name' }, { key: 'r', label: 'Role' },
              { key: 's', label: 'Status' }, { key: 'fc', label: 'FC Account' },
              { key: 'l', label: 'Last Login' }, { key: 'lc', label: 'Linked Chatter' },
              // 28-10-2026 (Jordy): commissie en wallet alleen voor owner/admin.
              ...(volledig
                ? [{ key: 'c', label: 'Commission' }, { key: 'w', label: 'Payout wallet' }]
                : []),
              { key: 'a', label: '' },
            ]} emptyText="No users">
              {(users.data ?? []).map((u) => {
                const c = (comp.data ?? []).find((x) => x.chatter_id === u.linked_chatter_id)
                const ch = (chatters.data ?? []).find((x) => x.id === u.linked_chatter_id)
                return (
                  <tr key={u.id}>
                    <td><b><Avatar name={u.username} />{u.username}</b></td>
                    <td className="mut2">{u.email}</td>
                    <td>{u.display_name || '—'}</td>
                    <td><Tag kind={u.role === 'owner' ? 'acc' : 'mut'}>{u.role}</Tag></td>
                    <td>{u.active ? <Tag kind="ok">Active</Tag> : <Tag kind="mut">Off</Tag>}</td>
                    <td>{u.fancentro_credentials_configured
                      ? <Tag kind="ok">configured</Tag>
                      : <span className="dim">—</span>}</td>
                    <td className="mut2" style={{ fontSize: 12 }}>
                      {u.last_login_at ? dt(u.last_login_at) : 'Never'}
                    </td>
                    <td className="mut">{ch?.name || '—'}</td>
                    {volledig ? (
                      <>
                        <td className="num mono">
                          {c ? pct(c.commission_pct ?? c.default_commission_pct) : '—'}
                        </td>
                        <td className="mut2 mono" style={{ fontSize: 12 }}>
                          {c?.payout_wallet_address || '—'}
                        </td>
                      </>
                    ) : null}
                    <td>
                      <div className="row" style={{ gap: 6 }}>
                        <button className="btn ghost sm" type="button"
                                onClick={() => {
                                  setUserForm({
                                    username: u.username, email: u.email,
                                    display_name: u.display_name || '',
                                    password: '', role: u.role,
                                    linked_chatter_id: u.linked_chatter_id == null
                                      ? '' : String(u.linked_chatter_id),
                                    allowed_pages: u.allowed_pages ?? [],
                                    active: u.active,
                                    timezone: u.timezone ?? '',
                                    content_download_max: u.content_download_max == null
                                      ? '' : String(u.content_download_max),
                                    download_cap: u.download_cap == null ? '' : String(u.download_cap),
                                  })
                                  setUserEdit(u)
                                }}>
                          Edit
                        </button>
                        <button
                          className="btn ghost sm"
                          type="button"
                          onClick={() => run({
                            label: 'Toggle user',
                            confirm: `${u.active ? 'Deactivate' : 'Activate'} ${u.username}?`,
                            request: mut.patch(`/api/admin/auth/users/${u.id}`, { active: !u.active }),
                            onDone: users.refetch,
                            successText: `${u.username} ${u.active ? 'deactivated' : 'activated'}`,
                          })}
                        >
                          {u.active ? 'Deactivate' : 'Activate'}
                        </button>
                        {ikBenOwner && u.role !== 'owner' && u.id !== ikBen?.id ? (
                          <button className="btn danger sm" type="button"
                                  onClick={() => setUserWeg(u)}>
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </Table>
          )}
        </Panel>
        {userWeg ? (
          <VerwijderDialog
            titel="Delete login"
            naam={userWeg.username}
            request={mut.del(`/api/admin/auth/users/${userWeg.id}`)}
            onDone={users.refetch}
            onClose={() => setUserWeg(null)}
          >
            <p style={{ fontSize: 13, marginTop: 0 }}>
              <b>{userWeg.username}</b> can no longer log in and disappears from this list.
              The username and email become free to use again.
            </p>
            {userWeg.role === 'chatter' && userWeg.linked_chatter_id != null ? (
              <ul style={{ fontSize: 12.5, margin: '0 0 12px 18px', padding: 0 }}>
                <li>
                  <b>The chatter profile is deleted too</b> (#{userWeg.linked_chatter_id}): it disappears
                  from the roster, the shift agenda and every picker; assigned models, monitor rights
                  and future shifts are removed.
                </li>
                <li>Kept: payouts, sale assignments, manual earnings, worked shifts, chat history, audit log.
                  An open balance stays visible in Earnings until it is paid.</li>
              </ul>
            ) : (
              <ul style={{ fontSize: 12.5, margin: '0 0 12px 18px', padding: 0 }}>
                <li>Kept: audit log and send history.</li>
                <li>Removed: the link to the chatter profile{userWeg.linked_chatter_id != null ? ` (#${userWeg.linked_chatter_id})` : ''}; the profile itself stays.</li>
              </ul>
            )}
          </VerwijderDialog>
        ) : null}

          <Panel
            title="Agency Accounts"
            desc="Each agency has its own credentials and creator models."
            flush
            right={<button className="btn sm" type="button"
                           onClick={() => setAgencyOpen(true)}>
                     + Add Agency
                   </button>}
          >
            {agencies.loading ? <Loading /> : (
              <Table cols={[
                { key: 'n', label: 'Name' }, { key: 'p', label: 'Platform' },
                { key: 'e', label: 'Email' }, { key: 't', label: 'Type' },
                { key: 's', label: 'Status' }, { key: 'c', label: 'Creators', num: true },
                { key: 'l', label: 'Last Connected' }, { key: 'a', label: '' },
              ]} emptyText="No agency accounts">
                {(agencies.data ?? []).map((a) => (
                  <tr key={a.id}>
                    <td><b>{a.name}</b></td>
                    <td><Plat value={a.platform} /></td>
                    <td className="mut2">{a.email || '—'}</td>
                    <td className="mut">{a.is_test ? 'Test' : 'Production'}</td>
                    <td>{a.is_active ? <Tag kind="ok">Active</Tag> : <Tag kind="mut">Off</Tag>}</td>
                    <td className="num mono">{num(a.connected_creators)}</td>
                    <td className="dim">{a.last_connected_at ? dt(a.last_connected_at) : 'Never'}</td>
                    <td>
                      <button
                        className="btn ghost sm"
                        type="button"
                        onClick={() => run({
                          label: 'Delete agency',
                          confirm: `Delete agency "${a.name}"?`,
                          request: mut.del(`/api/admin/dashboard/agencies/${a.id}`),
                          onDone: agencies.refetch,
                        })}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}
          </Panel>

        </>
      ) : null}

      {/* 28-10-2026 (Jordy): Compensation & activity, verhuisd van Chatters.
          Vervangt het oude "Chatter Management" en zijn Edit-dialoog; alleen
          owner/admin (de tab staat voor anderen niet in de balk, en de
          component toont zelf ook niets zonder recht). */}
      {tab === 'compensation' && volledig ? (
        <>
          <div className="filters" style={{ justifyContent: 'flex-end' }}>
            <button className="btn sm" type="button"
                    onClick={() => navigate('/chatters')}>
              Open Chatters
            </button>
          </div>
          <CompensatieInstellingen />
        </>
      ) : null}
      </>
      {/* Gebruiker aanmaken of bewerken. Dezelfde velden als op oud: rol,
          actief, gekoppeld chatterprofiel en de paginarechten. Het wachtwoord
          is bij bewerken optioneel — leeg laten betekent ongewijzigd. */}
      {userNieuw || userEdit ? (
        <Modal
          title={userEdit ? `Edit user — ${userEdit.username}` : 'Add user'}
          width={560}
          onClose={() => { setUserNieuw(false); setUserEdit(null) }}
          footer={
            <>
              <button className="btn ghost" type="button"
                      onClick={() => { setUserNieuw(false); setUserEdit(null) }}>
                Cancel
              </button>
              <button
                className="btn primary"
                type="button"
                disabled={!!busy}
                onClick={() => {
                  const gekoppeld = userForm.linked_chatter_id === ''
                    ? null : Number(userForm.linked_chatter_id)
                  // Leeg = de standaardrechten van de rol, net als op oud.
                  const paginas = userForm.allowed_pages.length
                    ? userForm.allowed_pages : null
                  if (userEdit && !ikBenOwner && ikBenAdmin) {
                    run({
                      label: 'Save user',
                      request: mut.patch(`/api/admin/auth/users/${userEdit.id}/mass-messages-page`, {
                        enabled: userForm.allowed_pages.includes('mass_messages_page'),
                      }),
                      onDone: () => { users.refetch(); setUserEdit(null) },
                      successText: `${userForm.username} saved`,
                    })
                  } else if (userEdit) {
                    const body: Record<string, unknown> = {
                      username: userForm.username.trim(),
                      email: userForm.email.trim(),
                      display_name: userForm.display_name.trim(),
                      role: userForm.role,
                      active: userForm.active,
                      linked_chatter_id: gekoppeld,
                      // Bij bewerken ook een lege lijst meesturen: null betekent
                      // voor de backend "niet wijzigen", waardoor je de pagina's
                      // nooit helemaal uit kon zetten (27-09, D3). Leeg = de
                      // standaardrechten van de rol, net als op oud.
                      allowed_pages: userForm.allowed_pages,
                      // 07-10-2026 (Jordy): tijdzone-voorkeur; leeg = Amsterdam.
                      timezone: userForm.timezone,
                    }
                    // 23-10-2026 (Content fase 2): leeg = standaard van de rol.
                    if (['manager', 'chatter'].includes(userForm.role)) {
                      body.content_download_max = userForm.content_download_max.trim() === ''
                        ? null : Number(userForm.content_download_max)
                      body.download_cap = userForm.download_cap.trim() === ''
                        ? null : Number(userForm.download_cap)
                    }
                    if (userForm.password.trim()) body.new_password = userForm.password.trim()
                    run({
                      label: 'Save user',
                      request: mut.patch(`/api/admin/auth/users/${userEdit.id}`, body),
                      onDone: () => { users.refetch(); setUserEdit(null) },
                      successText: `${userForm.username} saved`,
                    })
                  } else {
                    run({
                      label: 'Create user',
                      request: mut.post('/api/admin/auth/users', {
                        username: userForm.username.trim(),
                        email: userForm.email.trim(),
                        display_name: userForm.display_name.trim() || userForm.username.trim(),
                        password: userForm.password,
                        role: userForm.role,
                        linked_chatter_id: gekoppeld,
                        allowed_pages: paginas,
                      }),
                      onDone: () => { users.refetch(); setUserNieuw(false) },
                      successText: `${userForm.username} created`,
                    })
                  }
                }}
              >
                {userEdit ? 'Save' : 'Create'}
              </button>
            </>
          }
        >
          <Field label="Username">
            <input className="inp" value={userForm.username}
                   onChange={(e) => setUserForm((f) => ({ ...f, username: e.target.value }))} />
          </Field>
          <Field label="Email">
            <input className="inp" type="email" value={userForm.email}
                   onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} />
          </Field>
          <Field label="Display name">
            <input className="inp" value={userForm.display_name}
                   onChange={(e) => setUserForm((f) => ({ ...f, display_name: e.target.value }))} />
          </Field>
          <Field label={userEdit ? 'New password' : 'Password'}
                 hint={userEdit ? 'Leave empty to keep the current password.' : undefined}>
            {/* 05-10-2026 (Jordy): nooit door de browser laten invullen; leeg = ongewijzigd. */}
            <input className="inp" type="password" autoComplete="new-password" name="nieuw-wachtwoord"
                   value={userForm.password}
                   onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} />
          </Field>
          <Field label="Role">
            <select className="inp" value={userForm.role}
                    onChange={(e) => {
                      const rol = e.target.value
                      // 05-10-2026: een nieuw account begint met de standaardrechten van zijn rol;
                      // bij bewerken blijven de bestaande rechten staan.
                      setUserForm((f) => ({ ...f, role: rol, allowed_pages: userEdit ? f.allowed_pages : standaardTokens(rol) }))
                    }}>
              {ROLLEN.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Linked chatter profile"
                 hint="Chatters only see conversations for their assigned models.">
            <select className="inp" value={userForm.linked_chatter_id}
                    onChange={(e) => setUserForm((f) => ({ ...f, linked_chatter_id: e.target.value }))}>
              <option value="">None</option>
              {(chatters.data ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name} (ID: {c.id})</option>
              ))}
            </select>
          </Field>
          {userEdit ? (
            <Field label="Time zone"
                   hint="Mass Messages: dates and times are entered and shown in this zone. Empty = Amsterdam.">
              <ZoneKeuze value={userForm.timezone} leegLabel="Default (Europe/Amsterdam)"
                         onChange={(z) => setUserForm((f) => ({ ...f, timezone: z }))} />
            </Field>
          ) : null}
          {userEdit ? (
            <label className="chkrow">
              <span className={userForm.active ? 'chk on' : 'chk'}
                    onClick={() => setUserForm((f) => ({ ...f, active: !f.active }))}>
                {userForm.active ? '\u2713' : ''}
              </span>
              {userForm.active ? 'Active — can log in' : 'Inactive — login blocked'}
            </label>
          ) : null}
          {/* 05-10-2026 (Jordy): één gedeeld rechtenblok, ook in Chatters
              (aanmaken en Edit chatter). Alleen een owner wijzigt. */}
          <RechtenBlok rol={userForm.role} pages={userForm.allowed_pages} magWijzigen={ikBenOwner}
                       adminMag={ikBenAdmin && !!userEdit} isLead={false}
                       onChange={(pages) => setUserForm((f) => ({ ...f, allowed_pages: pages }))} />
          {/* 23-10-2026 (Jordy, Content fase 2): exfiltratierem per gebruiker. */}
          {userEdit && ikBenOwner && ['manager', 'chatter'].includes(userForm.role)
            && userForm.allowed_pages.includes('content_download') ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
              <Field label="Max items per download"
                     hint="Empty = default (10). One file or one ZIP may hold at most this many.">
                <input className="inp" inputMode="numeric" placeholder="10"
                       value={userForm.content_download_max}
                       onChange={(e) => setUserForm((f) => ({ ...f, content_download_max: e.target.value.replace(/\D/g, '') }))} />
              </Field>
              <Field label="Max downloads per 24 hours"
                     hint={`Empty = default (${userForm.role === 'manager' ? 200 : 20}). Every item counts.`}>
                <input className="inp" inputMode="numeric"
                       placeholder={userForm.role === 'manager' ? '200' : '20'}
                       value={userForm.download_cap}
                       onChange={(e) => setUserForm((f) => ({ ...f, download_cap: e.target.value.replace(/\D/g, '') }))} />
              </Field>
            </div>
          ) : null}
        </Modal>
      ) : null}

      {agencyOpen ? (
        <Modal
          title="Add agency"
          desc="Same fields as the old form; sent as a POST to dashboard/agencies."
          onClose={() => setAgencyOpen(false)}
          footer={
            <>
              <button className="btn ghost sm" type="button" onClick={() => setAgencyOpen(false)}>
                Cancel
              </button>
              <button
                className="btn primary sm"
                type="button"
                disabled={!agency.name || busy === 'Add Agency'}
                onClick={() => run({
                  label: 'Add Agency',
                  request: () => api.post('/api/admin/dashboard/agencies', agency),
                  onDone: () => { setAgencyOpen(false); agencies.refetch() },
                  successText: 'Agency added.',
                })}
              >
                Save
              </button>
            </>
          }
        >
          <Field label="Name">
            <input className="inp" value={agency.name}
                   onChange={(e) => setAgency((a) => ({ ...a, name: e.target.value }))} />
          </Field>
          <Field label="Platform">
            <select className="inp" value={agency.platform}
                    onChange={(e) => setAgency((a) => ({ ...a, platform: e.target.value }))}>
              <option value="fancentro">FanCentro</option>
              <option value="f2f">F2F</option>
            </select>
          </Field>
          <Field label="Email">
            <input className="inp" value={agency.email}
                   onChange={(e) => setAgency((a) => ({ ...a, email: e.target.value }))} />
          </Field>
          <Field label="Password">
            <input className="inp" type="password" value={agency.password}
                   onChange={(e) => setAgency((a) => ({ ...a, password: e.target.value }))} />
          </Field>
          <Field label="TOTP secret" hint="Leave empty if there is no 2FA.">
            <input className="inp" value={agency.totp_secret}
                   onChange={(e) => setAgency((a) => ({ ...a, totp_secret: e.target.value }))} />
          </Field>
          <label className="chkrow">
            <span className={agency.is_active ? 'chk on' : 'chk'}
                  onClick={() => setAgency((a) => ({ ...a, is_active: !a.is_active }))}>
              {agency.is_active ? '✓' : ''}
            </span>
            Active
          </label>
          <label className="chkrow">
            <span className={agency.is_test ? 'chk on' : 'chk'}
                  onClick={() => setAgency((a) => ({ ...a, is_test: !a.is_test }))}>
              {agency.is_test ? '✓' : ''}
            </span>
            Test account
          </label>
        </Modal>
      ) : null}
    </>
  )
}

